/**
 * Fachwörter, erklärt an der Stelle, an der sie stehen.
 *
 * Ein Schwarmtest mit jemandem ohne Bootsbezug hat 25 Wörter gezählt, die ihm nichts
 * sagen, vom Topplicht bis zur Unsichtigkeit. Das ist keine Schwäche der App, sondern ihr
 * Gegenstand: die Sache hat diese Wörter, und wer die Prüfung macht, muss sie können. Sie
 * wegzuübersetzen wäre falsch, sie unerklärt stehen zu lassen auch.
 *
 * Also die dritte Möglichkeit: das Wort bleibt stehen und erklärt sich, wo es steht. Zwei
 * Sätze, angetippt, wieder weg. Kein Glossar-Menüpunkt, den niemand öffnet, und keine
 * Fußnoten, die den Text zerhacken.
 *
 * Die Erklärungen sind nicht die Norm, sondern der Satz davor: was das Ding ist, und
 * warum es einen interessiert. Wo die Norm eine Zahl nennt, steht sie hier mit ihr.
 */

export interface Begriff {
  /** Das Wort, wie es im Text steht. */
  wort: string
  /** Weitere Schreibweisen, die dasselbe meinen. */
  auch?: string[]
  erklaerung: { de: string; en: string }
}

export const BEGRIFFE: Begriff[] = [
  {
    wort: 'Topplicht',
    auch: ['Topplichter', 'Topplichtes'],
    erklaerung: {
      de: 'Das weiße Licht über dem Vorschiff, das nach vorn und zu beiden Seiten bis 22,5 Grad hinter querab scheint. Wer es sieht, hat das Fahrzeug von vorn oder schräg von vorn.',
      en: 'The white light over the bow, visible ahead and to both sides up to 22.5 degrees abaft the beam. If you see it, the vessel is heading towards you or across.',
    },
  },
  {
    wort: 'Seitenlichter',
    auch: ['Seitenlicht'],
    erklaerung: {
      de: 'Grün an Steuerbord, rot an Backbord, jedes über 112,5 Grad. Sie sagen, wohin ein Fahrzeug fährt: rot heißt, es zieht nach links vor dir vorbei, grün nach rechts.',
      en: 'Green to starboard, red to port, each over 112.5 degrees. They say where a vessel is going: red means it passes to your left, green to your right.',
    },
  },
  {
    wort: 'Hecklicht',
    erklaerung: {
      de: 'Das weiße Licht am Heck, sichtbar über 135 Grad nach achtern. Siehst du nur dieses, fährt das Fahrzeug von dir weg.',
      en: 'The white light at the stern, visible over 135 degrees astern. If it is all you see, the vessel is moving away from you.',
    },
  },
  {
    wort: 'Rundumlicht',
    auch: ['Rundumlichter', 'von allen Seiten sichtbares Licht'],
    erklaerung: {
      de: 'Ein Licht, das über volle 360 Grad scheint. Es sagt nichts über die Richtung, dafür etwas über den Zustand: Anker, Manövrierunfähigkeit, gefährliche Güter.',
      en: 'A light visible over the full 360 degrees. It says nothing about direction, but something about the state: anchored, not under command, dangerous cargo.',
    },
  },
  {
    wort: 'Peilung',
    erklaerung: {
      de: 'Der Winkel, unter dem du auf das Fahrzeug siehst, von seinem Bug aus gezählt. Null heißt recht voraus, 90 heißt querab von Steuerbord.',
      en: 'The angle you are looking at the vessel from, counted from its bow. Zero is dead ahead, 90 is abeam to starboard.',
    },
  },
  {
    wort: 'Tragweite',
    erklaerung: {
      de: 'Wie weit ein Licht bei klarer Luft zu sehen ist, in Seemeilen. Die Regeln schreiben sie nach Länge des Fahrzeugs vor, von zwei bis sechs Seemeilen.',
      en: 'How far a light carries in clear air, in nautical miles. The rules set it by the length of the vessel, from two to six miles.',
    },
  },
  {
    wort: 'Seemeile',
    auch: ['Seemeilen', 'sm'],
    erklaerung: {
      de: 'Eine Seemeile sind 1852 Meter, also eine Bogenminute auf einem Längengrad. Entfernungen auf dem Wasser werden darin gemessen, nicht in Kilometern.',
      en: 'A nautical mile is 1852 metres, one minute of arc on a meridian. Distances on the water are measured in these, not in kilometres.',
    },
  },
  {
    wort: 'Lichterbild',
    auch: ['Lichterbilder', 'Lichterführung'],
    erklaerung: {
      de: 'Die Zusammenstellung aller Lichter, die ein Fahrzeug in einer bestimmten Lage führen muss. Aus ihr liest man ab, was für ein Fahrzeug das ist und was es gerade tut.',
      en: 'The set of lights a vessel must carry in a given situation. From it you read what kind of vessel it is and what it is doing.',
    },
  },
  {
    wort: 'Seezeichen',
    erklaerung: {
      de: 'Tonnen, Baken und Feuer, die das Fahrwasser markieren. Ihre Farbe, Form und Kennung sagen, auf welcher Seite du vorbeifährst und wo die Gefahr liegt.',
      en: 'Buoys, beacons and lights that mark the channel. Their colour, shape and rhythm say which side to pass and where the danger lies.',
    },
  },
  {
    wort: 'Kennung',
    erklaerung: {
      de: 'Der Rhythmus, in dem ein Feuer blinkt, zum Beispiel "Fl.R.4s" für ein rotes Blitzfeuer alle vier Sekunden. Zwei Feuer nebeneinander unterscheidet man daran und nicht an der Farbe.',
      en: 'The rhythm a light flashes in, for example "Fl.R.4s" for a red flash every four seconds. Two nearby lights are told apart by this, not by colour.',
    },
  },
  {
    wort: 'Backbord',
    erklaerung: {
      de: 'Die linke Seite des Fahrzeugs, von achtern nach vorn gesehen. Sie führt das rote Seitenlicht.',
      en: 'The left side of the vessel, looking forward. It carries the red sidelight.',
    },
  },
  {
    wort: 'Steuerbord',
    erklaerung: {
      de: 'Die rechte Seite des Fahrzeugs, von achtern nach vorn gesehen. Sie führt das grüne Seitenlicht und hat im Zweifel Vorfahrt.',
      en: 'The right side of the vessel, looking forward. It carries the green sidelight and in case of doubt has right of way.',
    },
  },
  {
    wort: 'Schleppverband',
    auch: ['Schleppverbands', 'Schleppen'],
    erklaerung: {
      de: 'Ein Fahrzeug, das ein anderes hinter sich herzieht, samt allem, was daran hängt. Die Länge des Schleppzugs entscheidet über die Lichter, und ein Schleppzug kann nicht ausweichen wie ein einzelnes Fahrzeug.',
      en: 'A vessel towing another behind it, together with everything attached. The length of the tow decides the lights, and a tow cannot manoeuvre like a single vessel.',
    },
  },
  {
    wort: 'Schubverband',
    auch: ['Schubverbands'],
    erklaerung: {
      de: 'Ein Fahrzeug, das andere vor sich herschiebt, auf Binnenwasserstraßen der Normalfall. Er wird wie ein einziges langes Fahrzeug behandelt.',
      en: 'A vessel pushing others ahead of it, the normal case on inland waterways. It is treated as a single long vessel.',
    },
  },
  {
    wort: 'manövrierunfähig',
    auch: ['Manövrierunfähigkeit'],
    erklaerung: {
      de: 'Ein Fahrzeug, das wegen eines Schadens nicht mehr steuern kann, zum Beispiel mit Maschinenausfall. Es führt zwei rote Rundumlichter übereinander und weicht niemandem aus.',
      en: 'A vessel that cannot steer because of a breakdown, for example an engine failure. It carries two red all-round lights and gives way to nobody.',
    },
  },
  {
    wort: 'manövrierbehindert',
    erklaerung: {
      de: 'Ein Fahrzeug, das wegen seiner Arbeit nicht frei ausweichen kann, zum Beispiel ein Bagger oder ein Kabelleger. Es führt rot über weiß über rot.',
      en: 'A vessel that cannot keep out of the way because of the work it is doing, for example a dredger or a cable layer. It carries red over white over red.',
    },
  },
  {
    wort: 'tiefgangbehindert',
    erklaerung: {
      de: 'Ein großes Fahrzeug, das wegen seines Tiefgangs das Fahrwasser nicht verlassen kann. Es darf zusätzlich drei rote Rundumlichter übereinander führen.',
      en: 'A large vessel that cannot leave the channel because of its draught. It may carry three red all-round lights in addition.',
    },
  },
  {
    wort: 'Unsichtigkeit',
    erklaerung: {
      de: 'Nebel, dichter Regen, Schneetreiben: alles, was die Sicht nimmt. Dann gelten eigene Schallsignale, weil man sich nicht mehr sehen kann.',
      en: 'Fog, heavy rain, snow: anything that takes the visibility. Then separate sound signals apply, because you can no longer see each other.',
    },
  },
  {
    wort: 'Fahrwasser',
    erklaerung: {
      de: 'Der Teil des Gewässers, der für die Schifffahrt tief genug und markiert ist. Außerhalb davon ist nicht verboten, aber auf eigene Gefahr.',
      en: 'The part of the water deep enough for shipping and marked as such. Outside it is not forbidden, but at your own risk.',
    },
  },
  {
    wort: 'KVR',
    auch: ['Kollisionsverhütungsregeln'],
    erklaerung: {
      de: 'Die internationalen Regeln zur Verhütung von Zusammenstößen auf See, seit 1972 weltweit gleich. Sie gelten auf allen Meeren und in den meisten Küstengewässern.',
      en: 'The international regulations for preventing collisions at sea, the same worldwide since 1972. They apply on all oceans and in most coastal waters.',
    },
  },
  {
    wort: 'BinSchStrO',
    auch: ['Binnenschifffahrtsstraßen-Ordnung'],
    erklaerung: {
      de: 'Die deutsche Binnenschifffahrtsstraßen-Ordnung, die auf Rhein, Elbe und den anderen Binnenwasserstraßen gilt. Sie weicht von der See ab: ein Topplicht statt zweier, blaue Lichter bei gefährlichen Gütern.',
      en: 'The German inland waterway rules, applying on the Rhine, the Elbe and other inland waters. They differ from the sea: one masthead light instead of two, blue lights for dangerous cargo.',
    },
  },
  {
    wort: 'Kardinalzeichen',
    erklaerung: {
      de: 'Vier Seezeichen, die sagen, auf welcher Seite einer Gefahr das sichere Wasser liegt: Nord, Ost, Süd, West. Ihre Kegel zeigen die Richtung, ihr Funkelfeuer nennt sie.',
      en: 'Four marks saying on which side of a danger the safe water lies: north, east, south, west. Their cones show the direction, their quick flashes name it.',
    },
  },
  {
    wort: 'laterale Zeichen',
    auch: ['Laterale Zeichen', 'Lateralzeichen'],
    erklaerung: {
      de: 'Die Tonnen, die den Rand des Fahrwassers markieren, rot an der einen und grün an der anderen Seite. Welche Farbe wo liegt, hängt von der Region ab.',
      en: 'The buoys marking the edge of the channel, red on one side and green on the other. Which colour lies where depends on the region.',
    },
  },
  {
    wort: 'Tagzeichen',
    auch: ['Signalkörper'],
    erklaerung: {
      de: 'Schwarze Bälle, Kegel und Zylinder am Mast, die bei Tag dasselbe sagen wie die Lichter bei Nacht. Ein Ball heißt Anker, zwei Kegel mit den Spitzen aneinander heißen Fischerei.',
      en: 'Black balls, cones and cylinders at the mast, saying by day what the lights say by night. A ball means anchored, two cones point to point mean fishing.',
    },
  },
  {
    wort: 'Bergfahrer',
    auch: ['Bergfahrers'],
    erklaerung: {
      de: 'Ein Fahrzeug in Bergfahrt: auf einem Fluss zur Quelle hin, auf einem Kanal in der Richtung, die die Ordnung dort so nennt, und immer die Fahrt in einen Hafen hinein (§ 1.01 Nummer 40). Bei der Begegnung verlangt der Bergfahrer die Seite, der Talfahrer antwortet.',
      en: 'A vessel travelling upstream: on a river towards the source, on a canal in the direction the regulation names so, and always when entering a port (§ 1.01 No. 40). When two meet, the upstream vessel asks for the side and the downstream vessel answers.',
    },
  },
  {
    wort: 'Talfahrer',
    auch: ['Talfahrers'],
    erklaerung: {
      de: 'Ein Fahrzeug in Talfahrt, also in der Gegenrichtung zur Bergfahrt: auf einem Fluss mit dem Strom (§ 1.01 Nummer 41). Bei der Begegnung antwortet der Talfahrer auf das Zeichen des Bergfahrers.',
      en: 'A vessel travelling downstream, the opposite direction to upstream: on a river with the current (§ 1.01 No. 41). When two meet, she answers the signal of the upstream vessel.',
    },
  },
  {
    wort: 'Kleinfahrzeug',
    auch: ['Kleinfahrzeugs', 'Kleinfahrzeuge'],
    erklaerung: {
      de: 'Nach § 1.01 ein Fahrzeug, dessen Rumpf kürzer als 20 Meter ist, mit Ausnahmen wie Fähren und Fahrzeugen, die für mehr als zwölf Fahrgäste zugelassen sind: also fast jedes Sportboot. Von den Schallzeichen darf es nur die Allgemeinen geben, keine Begegnungs- oder Überholzeichen.',
      en: 'Under § 1.01 a vessel whose hull is shorter than 20 metres, with exceptions such as ferries and vessels licensed for more than twelve passengers: so nearly every leisure boat. Of the sound signals it may give only the general ones, not those for meeting or overtaking.',
    },
  },
  {
    wort: 'Verband',
    auch: ['Verbandes', 'Verbands'],
    erklaerung: {
      de: 'Ein Schleppverband, ein Schubverband oder gekuppelte Fahrzeuge (§ 1.01 Nummer 3). Ein Schallzeichen gibt nur das Fahrzeug, auf dem der Führer des Verbandes ist, beim Schleppverband das motorisierte Fahrzeug an der Spitze.',
      en: 'A towed convoy, a pushed convoy or coupled vessels (§ 1.01 No. 3). A sound signal is given only by the vessel carrying the master of the convoy, in a towed convoy by the motorised vessel at its head.',
    },
  },
]

const NACHSCHLAG = new Map<string, Begriff>()
for (const begriff of BEGRIFFE) {
  NACHSCHLAG.set(begriff.wort.toLowerCase(), begriff)
  for (const auch of begriff.auch ?? []) NACHSCHLAG.set(auch.toLowerCase(), begriff)
}

/** Alle Schreibweisen, längste zuerst: "Laterale Zeichen" vor "Zeichen". */
export const SCHREIBWEISEN: string[] = [...NACHSCHLAG.keys()].sort((a, b) => b.length - a.length)

export function begriff(wort: string): Begriff | undefined {
  return NACHSCHLAG.get(wort.toLowerCase())
}

export interface Stueck {
  text: string
  /** Gesetzt, wenn dieses Stück ein Fachwort ist. */
  begriff?: Begriff
}

/**
 * Zerlegt einen Text in Stücke, von denen manche Fachwörter sind.
 *
 * Jedes Wort wird höchstens einmal je Text ausgezeichnet: dreimal dasselbe Wort
 * anklickbar zu machen ist keine Hilfe mehr, sondern ein Teppich aus Knöpfen.
 */
export function zerlege(text: string): Stueck[] {
  const stuecke: Stueck[] = []
  const schon = new Set<string>()
  let rest = text
  while (rest.length > 0) {
    let treffer: { index: number; laenge: number; begriff: Begriff } | undefined
    for (const schreibweise of SCHREIBWEISEN) {
      const gefunden = NACHSCHLAG.get(schreibweise)!
      if (schon.has(gefunden.wort)) continue
      const index = rest.toLowerCase().indexOf(schreibweise)
      if (index < 0) continue
      // Nur ganze Wörter: "Anker" nicht in "Ankerkette".
      const davor = index === 0 ? ' ' : rest[index - 1]!
      const danach = rest[index + schreibweise.length] ?? ' '
      if (/[\p{L}]/u.test(davor) || /[\p{L}]/u.test(danach)) continue
      if (!treffer || index < treffer.index) {
        treffer = { index, laenge: schreibweise.length, begriff: gefunden }
      }
    }
    if (!treffer) {
      stuecke.push({ text: rest })
      break
    }
    if (treffer.index > 0) stuecke.push({ text: rest.slice(0, treffer.index) })
    stuecke.push({
      text: rest.slice(treffer.index, treffer.index + treffer.laenge),
      begriff: treffer.begriff,
    })
    schon.add(treffer.begriff.wort)
    rest = rest.slice(treffer.index + treffer.laenge)
  }
  return stuecke
}
